import deepmerge from 'deepmerge'
import { createNanoEvents } from 'nanoevents'
import {
  I18n as BaseI18n,
  useMakePlural as makePlural,
  type Scope,
  type TranslateOptions,
  type Dict,
  type I18nOptions,
} from 'i18n-js'
import { en as makePluralEn, pl as makePluralPl, de as makePluralDe } from 'make-plural'
import { AVAILABLE_LOCALES, type AvailableLocale } from '@/config/constants'
import { interpolate } from './intl/interpolate'
import { formatDuration, Duration, FormatDurationScope } from './intl/formatDuration'

export type AnyDate = Date | string | number
export type FormatDateTimeScope = 'default' | 'long' | 'short'
export type FormatDateScope = 'default' | 'long' | 'short'
export type FormatTimeScope = 'default' | 'long' | 'short'
export type { FormatDurationScope, Duration }

const allTranslations = Object.values(
  import.meta.glob<any>(
    [
      '@/../../config/locales/**/*.yml',
      '!@/../../config/locales/**/notifications.*.yml',
      '!@/../../config/locales/**/devise.*.yml',
    ],
    {
      eager: true,
      import: 'default',
    },
  ),
).reduce((translations, value) => deepmerge(translations, value))

export const translations = Object.fromEntries(
  AVAILABLE_LOCALES.map(locale => [locale, (allTranslations as any)[locale]]),
) as Record<AvailableLocale, any>

export type TranslateModelOptions = {
  model?: string | null
}

const translateModelSymbol = Symbol('translateModel')

class I18n extends BaseI18n {
  static new(translations?: Dict, options?: Partial<I18nOptions>): I18n {
    if (import.meta.hot) {
      if (!import.meta.hot.data.i18n) {
        import.meta.hot.data.i18n = new I18n(translations, options)
      }

      return import.meta.hot.data.i18n
    }

    return new I18n(translations, options)
  }

  constructor(translations?: Dict, options?: Partial<I18nOptions>) {
    super(translations, { ...options, missingBehavior: 'guess' })

    this.interpolate = interpolate
  }

  private emitter = createNanoEvents<{
    localeChanged: () => void
    hotReloaded: () => void
  }>()

  setLocale(locale: string) {
    this.locale = locale
    this.emitter.emit('localeChanged')
  }

  hotReload() {
    this.emitter.emit('hotReloaded')
  }

  onLocaleChange(callback: () => void) {
    return this.emitter.on('localeChanged', callback)
  }

  onHotReload(callback: () => void) {
    return this.emitter.on('hotReloaded', callback)
  }

  /**
   * Translate a model-specific key. Use 'MODEL' in the key as a placeholder for the model name or 'base'.
   *
   * ```
   * translateModel('models.MODEL.actions.delete', { model: 'location' })
   * ```
   *
   * Tries to find:
   * - models.location.actions.delete
   * - models.base.actions.delete
   *
   * Other than that, behaves exactly like `translate`.
   */
  translateModel<T = string>(
    scope: Scope,
    options?: TranslateModelOptions & TranslateOptions,
  ): string | T {
    if (!options?.model) {
      return super.translate<T>(scope, options)
    }

    const scopeString = typeof scope === 'string' ? scope : scope.join('.')
    const { model, ...rest } = options
    const modelScope = scopeString.replace('MODEL', options.model)
    const baseScope = scopeString.replace('MODEL', 'base')
    const opts = { ...options, defaultValue: translateModelSymbol }

    const t1 = super.translate(modelScope, opts)
    if ((t1 as any) !== translateModelSymbol) {
      return t1
    }

    const t2 = super.translate(baseScope, opts)
    if ((t2 as any) !== translateModelSymbol) {
      return t2
    }

    return this.missingTranslation.get(modelScope, rest)
  }

  /**
   * @alias {@link translateModel}
   */
  model = this.translateModel

  /**
   * Format date and time according to the format defined in the translations.
   */
  formatDateTime(
    date?: AnyDate | null,
    scope: FormatDateTimeScope = 'default',
    emptyValue: any = '—',
  ) {
    if (!date) {
      return emptyValue
    }

    const fullScope = `time.formats.${scope}`
    return this.toTime(fullScope, this.parseDate(date))
  }

  /**
   * Format date according to the format defined in the translations.
   */
  formatDate(date?: AnyDate | null, scope: FormatDateScope = 'default', emptyValue: any = '—') {
    if (!date) {
      return emptyValue
    }

    const fullScope = `date.formats.${scope}`
    return this.toTime(fullScope, this.parseDate(date))
  }

  /**
   * Format time according to the format defined in the translations.
   */
  formatTime(date?: AnyDate | null, scope: FormatTimeScope = 'default', emptyValue: any = '—') {
    if (!date) {
      return emptyValue
    }

    const formats: Record<FormatTimeScope, string> = {
      default: '%H:%M:%S',
      long: '%H:%M:%S',
      short: '%H:%M',
    }

    return this.strftime(this.parseDate(date), formats[scope])
  }

  /**
   * Format time according to the format defined in the translations.
   */
  formatTimeDuration(
    duration?: Duration | number | null,
    scope?: FormatDurationScope,
    format?: (keyof Duration)[] | undefined,
    emptyValue: any = '—',
  ) {
    if (typeof duration !== 'number') {
      return emptyValue
    }

    return formatDuration(this, duration, scope, format)
  }

  private parseDate(date: AnyDate): Date {
    if (date instanceof Date) {
      return date
    }

    if (typeof date === 'number') {
      return new Date(date)
    }

    const parsed = Date.parse(date)
    if (!isNaN(parsed)) {
      return new Date(parsed)
    }

    throw new Error(`Invalid date: ${date}`)
  }
}

const intl = I18n.new({
  en: translations.en,
  pl: translations.pl,
  de: translations.de,
})

intl.enableFallback = true
intl.defaultLocale = 'en'

intl.pluralization.register('en', makePlural({ pluralizer: makePluralEn }))
intl.pluralization.register('pl', makePlural({ pluralizer: makePluralPl }))
intl.pluralization.register('de', makePlural({ pluralizer: makePluralDe }))

export const setLocale = intl.setLocale.bind(intl)
export const t = intl.t.bind(intl)
export type Translate = typeof intl.t
export type I18nClass = I18n

export default intl

// #region Hot reload

if (import.meta.hot) {
  import.meta.hot.accept(newModule => {
    if (!newModule) {
      return
    }

    intl.store({
      en: newModule.translations.en,
      pl: newModule.translations.pl,
      de: newModule.translations.de,
    })

    intl.hotReload()
  })
}

// #endregion
